import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Divider,
  Chip,
  Stack,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { AutoAwesome, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import AccountSearchField from './AccountSearchField';
import { useBrand } from '../../../context/BrandContext';
import axios from 'axios';
import {
  reclassifyJournalEntry,
  getBulkReclassifyPreview,
  bulkReclassifyJournalEntry,
} from '../../../services/financialConfigService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Two modes:
 *   mode='classify'   (default) — POST /api/accounting/classification/manual/:transactionId
 *                                  for unjournaled bank transactions (Pending Queue flow).
 *   mode='reclassify' — POST /api/finance/journal-entries/:je_id/reclassify
 *                       voids the existing JE and creates a new one. Used from
 *                       P&L drill-down rows.
 *
 * Reclassify-mode props:
 *   journalEntryId       — required
 *   currentValues        — { debit_account_id, credit_account_id, brand_id, memo, source_description }
 *                          to pre-fill the form
 *   aiSuggestion         — { debit_account_id, credit_account_id, brand_id, memo, confidence, reasoning }
 *                          shown as an "Apply AI suggestion" chip
 */
export default function ManualClassificationModal({
  open,
  onClose,
  onSave,
  transaction,
  suggestedClassification,
  mode = 'classify',
  journalEntryId,
  currentValues,
  aiSuggestion,
  sourceRule,
}) {
  const { brands, selectedBrand } = useBrand();

  const [debitLines, setDebitLines] = useState([{ account_id: null, amount: '' }]);
  const [creditAccount, setCreditAccount] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [memo, setMemo] = useState('');
  const [saveAsRule, setSaveAsRule] = useState(false);
  const [excludeFromPL, setExcludeFromPL] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Bulk vendor reclassify — "fix this vendor across prior months" UX.
  // Loaded lazily when the user expands the section. Each candidate has a
  // checkbox so the user can deselect false-positives before submitting.
  const [bulkExpanded, setBulkExpanded] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState('');
  const [bulkEndDate, setBulkEndDate] = useState('');
  const [bulkCriteria, setBulkCriteria] = useState(null);
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkAllowClosed, setBulkAllowClosed] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Single-debit shorthand — read-only, used by validation, payload, and a few
  // helperText conditionals. Source of truth is `debitLines`.
  const debitAccount = debitLines[0]?.account_id ?? null;

  // Multi-line helpers
  const totalAmount = currentValues?.amount || 0;
  const debitSum = debitLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const isSplit = debitLines.length > 1;
  // Split debit lines must sum EXACTLY to the JE total, compared in integer
  // pence (Decimal(18,2) money) to match the server: the reclassify route and
  // the createJournalEntry balance guard reject anything a float tolerance here
  // would wave through. See never-do-decimal-math-in-javascript.md.
  const debitSumMatches = !isSplit || (totalAmount > 0 && Math.round(debitSum * 100) === Math.round(totalAmount * 100));

  const updateDebitLine = (idx, field, value) => {
    setDebitLines((lines) =>
      lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l))
    );
  };
  const addDebitLine = () => {
    setDebitLines((lines) => {
      // First time we go split, seed the new line's amount with the remaining
      // balance so the user lands on a balanced JE without extra typing.
      if (lines.length === 1) {
        const firstAmt = parseFloat(lines[0].amount) || totalAmount;
        const remainder = Math.max(0, totalAmount - firstAmt);
        return [
          { ...lines[0], amount: firstAmt ? String(firstAmt) : String(totalAmount) },
          { account_id: null, amount: remainder > 0 ? remainder.toFixed(2) : '' },
        ];
      }
      return [...lines, { account_id: null, amount: '' }];
    });
  };
  const removeDebitLine = (idx) => {
    setDebitLines((lines) => (lines.length > 1 ? lines.filter((_, i) => i !== idx) : lines));
  };
  const autoBalanceLastLine = () => {
    setDebitLines((lines) => {
      if (lines.length === 0) return lines;
      const allButLast = lines.slice(0, -1).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
      const balance = totalAmount - allButLast;
      return lines.map((l, i) =>
        i === lines.length - 1 ? { ...l, amount: balance > 0 ? balance.toFixed(2) : '0.00' } : l
      );
    });
  };

  // Initialize form with suggested values or transaction data
  useEffect(() => {
    if (!open) return;
    if (mode === 'reclassify' && currentValues) {
      // Pre-fill from existing JE state. If the original JE has multiple
      // debit lines (rare but possible), preserve the split.
      const existingDebits = (currentValues.lines || []).filter((l) => l.debit > 0);
      if (existingDebits.length > 1) {
        setDebitLines(
          existingDebits.map((l) => ({
            account_id: l.account_id,
            amount: Number(l.debit).toFixed(2),
          }))
        );
      } else {
        setDebitLines([{ account_id: currentValues.debit_account_id || null, amount: '' }]);
      }
      setCreditAccount(currentValues.credit_account_id || null);
      setBrandId(currentValues.brand_id || selectedBrand?.id || null);
      setMemo(currentValues.memo || currentValues.source_description || '');
      // UX nudge: default save-as-rule ON in reclassify mode. The most common
      // case is a recurring vendor (Greenleaf, etc.) that needs to land on the
      // same GL every month. Defaulting to OFF made users re-fix the same
      // vendor each month (Rishabh feedback 2026-05-11).
      setSaveAsRule(true);
      setExcludeFromPL(false);
      // Bulk reclassify panel resets — collapsed by default; preview fetches
      // on first expand. Range is empty here; the backend fills in FY defaults
      // on first preview call and the UI mirrors them back.
      setBulkExpanded(false);
      setBulkStartDate('');
      setBulkEndDate('');
      setBulkCriteria(null);
      setBulkCandidates([]);
      setBulkSelectedIds(new Set());
      setBulkLoading(false);
      setBulkError(null);
      setBulkAllowClosed(false);
      setBulkResult(null);
      return;
    }
    if (suggestedClassification) {
      setDebitLines([{
        account_id: suggestedClassification.suggested_debit_account || null,
        amount: '',
      }]);
      setCreditAccount(suggestedClassification.suggested_credit_account || suggestedClassification.suggested_account_1 || null);
      setBrandId(suggestedClassification.suggested_brand_1 || selectedBrand?.id || null);
      setMemo(suggestedClassification.transaction_description || transaction?.description || '');
    } else if (transaction) {
      setMemo(transaction.description || transaction.bank_description || '');
      setBrandId(selectedBrand?.id || null);
    }
  }, [open, mode, currentValues, suggestedClassification, transaction, selectedBrand]);

  // Fetch the bulk-reclassify preview for this JE within [bulkStartDate, bulkEndDate].
  // If start/end are blank, the backend applies its FY default and echoes it
  // back; we mirror those defaults into local state so the date inputs show
  // them.
  const fetchBulkPreview = async (overrides = {}) => {
    if (mode !== 'reclassify' || !journalEntryId) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const startDate = overrides.startDate !== undefined ? overrides.startDate : bulkStartDate;
      const endDate = overrides.endDate !== undefined ? overrides.endDate : bulkEndDate;
      const resp = await getBulkReclassifyPreview(journalEntryId, { startDate, endDate });
      if (!resp?.success) {
        setBulkError(resp?.error || 'Preview failed');
        setBulkCandidates([]);
        setBulkCriteria(null);
        return;
      }
      const d = resp.data || {};
      setBulkCriteria(d.criteria || null);
      // Mirror server-applied defaults back into the date inputs so the UI
      // shows the FY anchor instead of blanks on first load.
      if (!startDate && d.start_date) setBulkStartDate(d.start_date);
      if (!endDate && d.end_date) setBulkEndDate(d.end_date);
      const candidates = Array.isArray(d.candidates) ? d.candidates : [];
      setBulkCandidates(candidates);
      // Smart default: check rows that aren't already on the new target debit
      // account. (We don't know the new target yet if user hasn't picked one;
      // in that case check all non-split, non-closed-period rows.)
      const target = debitAccount;
      const initialChecked = new Set(
        candidates
          .filter((c) => !c.is_split)
          .filter((c) => !c.in_closed_period)
          .filter((c) => !target || c.current_debit_account_id !== target)
          .map((c) => c.je_id)
      );
      setBulkSelectedIds(initialChecked);
    } catch (err) {
      console.error('Bulk preview failed:', err);
      setBulkError(err?.response?.data?.error || err.message || 'Preview failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkExpand = async () => {
    setBulkExpanded(true);
    if (bulkCandidates.length === 0 && !bulkLoading) {
      await fetchBulkPreview();
    }
  };

  const toggleBulkRow = (jeId) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jeId)) next.delete(jeId);
      else next.add(jeId);
      return next;
    });
  };

  const submitBulkReclassify = async () => {
    if (!debitAccount) {
      setBulkError('Pick a new debit account in the form above first.');
      return;
    }
    if (bulkSelectedIds.size === 0) {
      setBulkError('Select at least one prior transaction to reclassify.');
      return;
    }
    const cleanMemo = (memo || '').trim();
    if (cleanMemo.length < 3) {
      setBulkError('Memo is required (used as the reclassification reason).');
      return;
    }
    setBulkLoading(true);
    setBulkError(null);
    try {
      const resp = await bulkReclassifyJournalEntry(journalEntryId, {
        newDebitAccountId: debitAccount,
        jeIdsToReclassify: Array.from(bulkSelectedIds),
        memo: cleanMemo,
        allowClosedPeriods: bulkAllowClosed,
      });
      if (!resp?.success) {
        setBulkError(resp?.error || 'Bulk reclassify failed');
        return;
      }
      setBulkResult(resp);
      // Refresh the candidate list so already-fixed rows now show on the new
      // account and disappear from default-checked.
      await fetchBulkPreview();
    } catch (err) {
      console.error('Bulk reclassify failed:', err);
      setBulkError(err?.response?.data?.error || err.message || 'Bulk reclassify failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    if (aiSuggestion.debit_account_id) {
      // AI suggestion is single-target — collapse to one debit line.
      setDebitLines([{ account_id: aiSuggestion.debit_account_id, amount: '' }]);
    }
    if (aiSuggestion.credit_account_id) setCreditAccount(aiSuggestion.credit_account_id);
    if (aiSuggestion.brand_id) setBrandId(aiSuggestion.brand_id);
    if (aiSuggestion.memo) setMemo(aiSuggestion.memo);
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    // Debit lines — at least one with an account; if split, all must have
    // accounts + positive amounts that sum to the JE total.
    if (!debitLines.length || !debitLines[0].account_id) {
      newErrors.debitAccount = 'Debit account is required';
    }
    if (isSplit) {
      debitLines.forEach((l, i) => {
        if (!l.account_id) newErrors[`debit_line_${i}`] = 'Account required';
        const amt = parseFloat(l.amount);
        if (!Number.isFinite(amt) || amt <= 0) {
          newErrors[`debit_line_${i}`] = (newErrors[`debit_line_${i}`] || '') + (newErrors[`debit_line_${i}`] ? ' • ' : '') + 'Amount must be > 0';
        }
      });
      if (!debitSumMatches) {
        newErrors.debit_sum = `Debit lines must sum to ${totalAmount.toFixed(2)} (currently ${debitSum.toFixed(2)})`;
      }
      const ids = debitLines.map((l) => l.account_id).filter(Boolean);
      if (new Set(ids).size !== ids.length) {
        newErrors.debit_sum = 'Each debit line must use a different account';
      }
    }

    if (!creditAccount) {
      newErrors.creditAccount = 'Credit account is required';
    }

    const debitIds = debitLines.map((l) => l.account_id).filter(Boolean);
    if (creditAccount && debitIds.includes(creditAccount)) {
      newErrors.creditAccount = 'Credit account must differ from every debit account';
    }

    if (!brandId) {
      newErrors.brandId = 'Brand is required';
    }

    if (!memo || memo.trim().length < 10) {
      newErrors.memo = 'Memo must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (createJournalEntry = false) => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        debitAccount,
        creditAccount,
        brandId,
        memo: memo.trim(),
      };

      if (mode === 'reclassify') {
        const splitPayload = isSplit
          ? {
              debitLines: debitLines.map((l) => ({
                accountId: l.account_id,
                amount: parseFloat(l.amount),
              })),
            }
          : {};
        const data = await reclassifyJournalEntry(journalEntryId, {
          ...payload,
          ...splitPayload,
          // Rule saving is single-target only; auto-skip when splitting.
          saveAsRule: isSplit ? false : saveAsRule,
          ruleDescription: currentValues?.source_description || '',
          ruleCounterparty: currentValues?.counterparty_name || '',
        });
        if (data.success) {
          onSave({ ...data, journalEntryCreated: true });
          handleClose();
        } else {
          setErrors({ submit: data.error || 'Reclassify failed' });
        }
        return;
      }

      // classify mode (existing flow)
      const token = localStorage.getItem('token');
      const transactionId = transaction?.id || transaction?.bank_transaction_id;
      const response = await axios.post(
        `${API_URL}/api/accounting/classification/manual/${transactionId}`,
        { ...payload, createJournalEntry },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        onSave({
          ...response.data.data,
          journalEntryCreated: createJournalEntry,
        });
        handleClose();
      }
    } catch (error) {
      console.error('Error saving classification:', error);
      setErrors({
        submit:
          error.response?.data?.error?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to save classification',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setDebitLines([{ account_id: null, amount: '' }]);
    setCreditAccount(null);
    setBrandId(null);
    setMemo('');
    setSaveAsRule(false);
    setExcludeFromPL(false);
    setErrors({});
    onClose();
  };

  // For reclassify, the transaction prop is optional — currentValues is the source of truth.
  if (mode !== 'reclassify' && !transaction) {
    return null;
  }

  // Unified summary fields (works for both modes)
  const summaryDate =
    (mode === 'reclassify' && currentValues?.entry_date) ||
    transaction?.date || transaction?.transaction_date || transaction?.bank_transaction_date;
  const summaryDescription =
    (mode === 'reclassify' && (currentValues?.source_description || currentValues?.memo)) ||
    transaction?.description || transaction?.bank_description;
  const summaryAmount =
    (mode === 'reclassify' && currentValues?.amount) ||
    transaction?.amount || transaction?.transaction_amount || transaction?.bank_amount;
  const summaryCurrency =
    (mode === 'reclassify' && currentValues?.currency) ||
    transaction?.currency || transaction?.bank_currency || 'USD';
  const summarySource =
    (mode === 'reclassify' && currentValues?.source_table) || transaction?.bank_source;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'reclassify' ? 'Reclassify Journal Entry' : 'Manual Classification'}
      </DialogTitle>

      <DialogContent>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submit}
          </Alert>
        )}

        {/* Transaction Summary */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            {mode === 'reclassify' ? 'Original Transaction' : 'Transaction Details'}
          </Typography>
          <Box sx={{ fontSize: '0.9rem', color: 'text.secondary', mt: 1 }}>
            {summaryDate && <Box><strong>Date:</strong> {summaryDate}</Box>}
            {summaryDescription && <Box><strong>Description:</strong> {summaryDescription}</Box>}
            {summaryAmount != null && (
              <Box>
                <strong>Amount:</strong> {summaryAmount} {summaryCurrency}
              </Box>
            )}
            {summarySource && <Box><strong>Source:</strong> {summarySource}</Box>}
          </Box>
        </Box>

        {/* AI suggestion (reclassify mode) */}
        {mode === 'reclassify' && aiSuggestion && (
          <Alert
            severity="info"
            icon={<AutoAwesome fontSize="small" />}
            action={
              <Button color="inherit" size="small" onClick={applyAiSuggestion}>
                Apply
              </Button>
            }
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              AI suggestion
              {aiSuggestion.confidence
                ? <Chip size="small" label={`${aiSuggestion.confidence}% confident`} sx={{ ml: 1 }} />
                : null}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
              {aiSuggestion.debit_account_id && (
                <span><strong>Debit:</strong> account #{aiSuggestion.debit_account_id}</span>
              )}
              {aiSuggestion.credit_account_id && (
                <span><strong>Credit:</strong> account #{aiSuggestion.credit_account_id}</span>
              )}
              {aiSuggestion.brand_id && (
                <span><strong>Brand:</strong> #{aiSuggestion.brand_id}</span>
              )}
            </Stack>
            {aiSuggestion.reasoning && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {aiSuggestion.reasoning}
              </Typography>
            )}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Exclude-from-P&L (reclassify mode only) — restricts debit picker to balance-sheet accounts */}
        {mode === 'reclassify' && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeFromPL}
                  onChange={(e) => setExcludeFromPL(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Exclude from P&L</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Restricts the debit picker to balance-sheet accounts (asset / liability / equity). Use for owner personal expenses, intercompany, or items that shouldn't hit P&L.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        )}

        {/* Debit Account(s) — single field by default; click "Add split line" to multi-line */}
        <Box sx={{ mb: 3 }}>
          {debitLines.map((line, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <AccountSearchField
                  label={isSplit ? `Debit (line ${i + 1}) *` : 'Debit Account *'}
                  value={line.account_id}
                  onChange={(v) => updateDebitLine(i, 'account_id', v)}
                  error={!!errors[`debit_line_${i}`] || (i === 0 && !!errors.debitAccount)}
                  filterAccountTypes={excludeFromPL ? ['asset', 'liability', 'equity'] : undefined}
                  helperText={
                    errors[`debit_line_${i}`] ||
                    (i === 0 && errors.debitAccount) ||
                    (i === 0
                      ? (excludeFromPL
                          ? 'Pick a balance-sheet account (asset / liability / equity)'
                          : 'Account to debit (increase Asset/Expense, decrease Liability/Revenue)')
                      : ' ')
                  }
                />
              </Box>
              {isSplit && (
                <Box sx={{ width: 140 }}>
                  <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    fullWidth
                    value={line.amount}
                    onChange={(e) => updateDebitLine(i, 'amount', e.target.value)}
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Box>
              )}
              {isSplit && (
                <Tooltip title="Remove line">
                  <IconButton
                    size="small"
                    onClick={() => removeDebitLine(i)}
                    sx={{ mt: 1 }}
                    aria-label="Remove debit line"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}

          {mode === 'reclassify' && totalAmount > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
              <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addDebitLine}>
                Add split line
              </Button>
              {isSplit && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color={debitSumMatches ? 'success.main' : 'error.main'}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    Sum: {debitSum.toFixed(2)} / {totalAmount.toFixed(2)}{' '}
                    {currentValues?.currency || ''}
                    {debitSumMatches ? ' ✓' : ` (off by ${(debitSum - totalAmount).toFixed(2)})`}
                  </Typography>
                  {!debitSumMatches && (
                    <Button size="small" onClick={autoBalanceLastLine}>
                      Auto-balance
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          )}

          {errors.debit_sum && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {errors.debit_sum}
            </Typography>
          )}
        </Box>

        {/* Credit Account */}
        <Box sx={{ mb: 3 }}>
          <AccountSearchField
            label="Credit Account *"
            value={creditAccount}
            onChange={setCreditAccount}
            error={!!errors.creditAccount}
            helperText={errors.creditAccount || 'Account to credit (increase Liability/Revenue, decrease Asset/Expense)'}
          />
        </Box>

        {/* Brand Selector */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth error={!!errors.brandId}>
            <InputLabel>Brand *</InputLabel>
            <Select
              value={brandId || ''}
              onChange={(e) => {
                setBrandId(e.target.value);
                setErrors({ ...errors, brandId: null });
              }}
              label="Brand *"
            >
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </Select>
            {errors.brandId && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.brandId}
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Memo */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Memo *"
            value={memo}
            onChange={(e) => {
              setMemo(e.target.value);
              setErrors({ ...errors, memo: null });
            }}
            multiline
            rows={3}
            fullWidth
            required
            error={!!errors.memo}
            helperText={errors.memo || `${memo.length} characters (min 10)`}
            placeholder="Enter a description for this journal entry"
          />
        </Box>

        {/* Save-as-rule (reclassify mode only). Disabled for split classifications —
            the rule helper points at one target account; split-rules need a separate path.
            When the JE came from an existing rule, the helper text explains that
            checking the box will UPDATE that rule (vs. creating a parallel one
            that would fight with the original). */}
        {mode === 'reclassify' && (
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              disabled={isSplit}
              control={
                <Checkbox
                  checked={!isSplit && saveAsRule}
                  onChange={(e) => setSaveAsRule(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {sourceRule
                      ? `Update rule "${sourceRule.ruleName}" for future transactions`
                      : 'Save as rule for similar transactions'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isSplit
                      ? 'Not available for split classifications.'
                      : sourceRule
                        ? `This transaction was auto-classified by rule "${sourceRule.ruleName}" (matches ${sourceRule.matchField}: ${sourceRule.matchValue}) → ${sourceRule.targetAccountCode} ${sourceRule.targetAccountName}. Checking this re-points the rule at your new debit account so future matches stop landing on the wrong GL.`
                        : 'Future transactions matching this description will auto-classify to the chosen debit account.'}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        )}

        {/* Bulk vendor reclassify — fix this vendor across N prior months.
            Collapsed by default; expands lazily to fetch the candidate list.
            Smart-default: only rows currently on a DIFFERENT debit account
            (i.e., not already on the new target) are pre-checked. */}
        {mode === 'reclassify' && (
          <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'grey.400', borderRadius: 1 }}>
            {!bulkExpanded ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Apply to prior months too?
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Find and reclassify other historical transactions matching this vendor.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={handleBulkExpand} disabled={!debitAccount}>
                  Show prior matches
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight="600">
                    Prior matches for this vendor
                  </Typography>
                  <Button size="small" onClick={() => setBulkExpanded(false)}>Hide</Button>
                </Box>

                {bulkCriteria && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Matching by {bulkCriteria.strategy === 'rule'
                      ? <>rule <strong>{bulkCriteria.ruleName}</strong> (pattern <code>{bulkCriteria.matchValue}</code>)</>
                      : <>counterparty <strong>{bulkCriteria.matchValue}</strong></>}
                  </Typography>
                )}
                {!bulkCriteria && !bulkLoading && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    No vendor pattern could be derived from this transaction (bulk reclassify only works for bank-sourced JEs with a rule or counterparty).
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'flex-end' }}>
                  <TextField
                    label="Start date"
                    type="date"
                    size="small"
                    value={bulkStartDate}
                    onChange={(e) => setBulkStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End date"
                    type="date"
                    size="small"
                    value={bulkEndDate}
                    onChange={(e) => setBulkEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button size="small" variant="outlined" onClick={() => fetchBulkPreview()} disabled={bulkLoading}>
                    {bulkLoading ? 'Loading…' : 'Reload'}
                  </Button>
                </Box>

                {bulkError && <Alert severity="error" sx={{ mb: 1 }}>{bulkError}</Alert>}

                {bulkCandidates.length === 0 && !bulkLoading && bulkCriteria && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    No prior matches in this date range. Try widening the range.
                  </Alert>
                )}

                {bulkCandidates.length > 0 && (
                  <>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      {bulkCandidates.map((c) => {
                        const isChecked = bulkSelectedIds.has(c.je_id);
                        const targetMatchesCurrent = debitAccount && c.current_debit_account_id === debitAccount;
                        return (
                          <Box
                            key={c.je_id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1,
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'grey.200',
                              backgroundColor: targetMatchesCurrent ? 'success.50' : (c.in_closed_period ? 'warning.50' : 'transparent'),
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              disabled={c.is_split || (c.in_closed_period && !bulkAllowClosed) || targetMatchesCurrent}
                              onChange={() => toggleBulkRow(c.je_id)}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {c.entry_date} · {Number(c.amount).toFixed(2)} {c.currency}
                                {' · '}
                                <strong>{c.current_debit_account_code}</strong> {c.current_debit_account_name}
                                {targetMatchesCurrent && ' (already on target)'}
                                {c.is_split && ' (split — skipped)'}
                                {c.in_closed_period && ` (${c.period_status})`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{
                                display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {c.memo}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {bulkCandidates.some((c) => c.in_closed_period) && (
                      <FormControlLabel
                        sx={{ mt: 1 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={bulkAllowClosed}
                            onChange={(e) => setBulkAllowClosed(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            Allow reclassifying JEs in closed periods (admin override)
                          </Typography>
                        }
                      />
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {bulkSelectedIds.size} selected of {bulkCandidates.length} candidates
                        {' · '}
                        Total: {Array.from(bulkSelectedIds)
                          .map((id) => bulkCandidates.find((c) => c.je_id === id)?.amount || 0)
                          .reduce((s, n) => s + Number(n), 0)
                          .toFixed(2)}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={bulkLoading || bulkSelectedIds.size === 0 || !debitAccount}
                        onClick={submitBulkReclassify}
                      >
                        {bulkLoading ? 'Reclassifying…' : `Reclassify ${bulkSelectedIds.size} prior matches`}
                      </Button>
                    </Box>

                    {bulkResult && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Reclassified {bulkResult.counts?.succeeded || 0} of {bulkResult.counts?.requested || 0} JEs.
                        {bulkResult.counts?.failed > 0 && ` ${bulkResult.counts.failed} failed.`}
                        {bulkResult.counts?.skipped_closed_period > 0 && ` ${bulkResult.counts.skipped_closed_period} skipped (closed period).`}
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        {mode === 'reclassify' ? (
          <Button onClick={() => handleSave(true)} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Reclassify'}
          </Button>
        ) : (
          <>
            <Button onClick={() => handleSave(false)} variant="outlined" disabled={saving}>
              Save Classification
            </Button>
            <Button onClick={() => handleSave(true)} variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Save & Create Entry'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
